export interface ApplicationSummary {
  id: string;
  name: string;
  type: string;
  owner: string;
  environment: string;
  status: 'Running' | 'Failed';
  updatedAt: string;
  url: string | null;
  /** e.g. "mumzworld-tech/sre-idp-catalog-api" — null for an app deployed
   * before source-repo tagging existed (2026-09-12). */
  repoFullName: string | null;
  /** The actual git branch built into the running image — null under the
   * same condition as repoFullName above. */
  branch: string | null;
  /** Department slug — null for an app deployed before department tagging
   * existed (2026-09-13). */
  department: string | null;
}

/** A department in the platform's taxonomy (see platform-controller's
 * departments module) — used to prefix a newly-created repo's name and to
 * categorize an app in "My Applications". */
export interface Department {
  /** e.g. "customer-support" — goes into the repo name and the
   * MUMZWORLD_DEPARTMENT tag. */
  slug: string;
  /** e.g. "Customer Support" — display label only. */
  name: string;
}

export type CreateKind = 'frontend' | 'backend' | 'ai-agent' | 'worker' | 'scheduled-job' | 'fullstack' | 'compose';

export interface CreateKindConfig {
  kind: CreateKind;
  route: string;
  title: string;
  /** Golden template this kind deploys with — undefined means no real
   * template exists yet, so the form renders but submission is disabled
   * with an honest "coming soon" message instead of faking success. */
  template?: string;
}

export const CREATE_KIND_CONFIG: Record<CreateKind, CreateKindConfig> = {
  frontend: { kind: 'frontend', route: '/create/frontend', title: 'Frontend App', template: 'frontend-react-vite' },
  backend: { kind: 'backend', route: '/create/backend', title: 'Backend Service', template: 'backend-node-nestjs' },
  'ai-agent': { kind: 'ai-agent', route: '/create/ai-agent', title: 'AI Agent', template: 'ai-agent-python' },
  worker: { kind: 'worker', route: '/create/worker', title: 'Worker', template: 'worker-node' },
  'scheduled-job': {
    kind: 'scheduled-job',
    route: '/create/scheduled-job',
    title: 'Scheduled Job',
    template: 'scheduled-job-node',
  },
  // No single `template` — this kind deploys two fixed golden templates
  // (backend-node-nestjs + frontend-react-vite) together, handled by
  // CreateFlowPage's fullstack branch and a dedicated API call/workflow.
  fullstack: { kind: 'fullstack', route: '/create/fullstack', title: 'Full Stack App' },
  // Paste a docker-compose file, get a URL — no repo, no template. Has its
  // own page (ComposeAppPage), not CreateFlowPage.
  compose: { kind: 'compose', route: '/create/compose', title: 'Compose App' },
};

export type WorkflowStatus =
  | { phase: 'idle' }
  | { phase: 'submitting' }
  | { phase: 'polling'; workflowId: string; status: string }
  | { phase: 'done'; result: Record<string, unknown> }
  | { phase: 'error'; message: string };
